import { Directive, ElementRef, Input, OnDestroy, Renderer2, inject, effect, signal, ChangeDetectorRef, NgZone } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DestroyRef } from '@angular/core';

type ViewMode = 'horizontal' | 'vertical';
type ClassMap = Partial<Record<ViewMode, string>>;
type StyleMap = Partial<Record<ViewMode, Partial<CSSStyleDeclaration>>>;

@Directive({
  selector: '[lxResponsiveClass]',
  standalone: true,
})
export class LxResponsiveClassDirective implements OnDestroy {
  private bo = inject(BreakpointObserver);
  private host = inject(ElementRef<HTMLElement>);
  private r2 = inject(Renderer2);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private zone = inject(NgZone);

  private viewMode = signal<ViewMode>('horizontal');

  /** Example: { horizontal: 'div-card-horizontal', vertical: 'div-card-handset' } */
  @Input('lxResponsiveClass') classMap: ClassMap = {};
  /** Optional: { horizontal: { marginLeft: '20px' }, vertical: { marginTop: '60px' } } */
  @Input() kbResponsiveStyle?: StyleMap;

  private prevApplied: string[] = [];
  private prevAppliedStyles: string[] = [];
  private resizeTimeout?: number;

  constructor() {
    const sub = this.bo.observe([Breakpoints.HandsetPortrait]).subscribe(state => {
      this.zone.run(() => {
        this.viewMode.set(state.matches ? 'vertical' : 'horizontal');
      });
    });

    const resizeHandler = () => {
      if (this.resizeTimeout) {
        window.clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = window.setTimeout(() => {
        this.zone.run(() => {
          const isHandset = this.bo.isMatched(Breakpoints.HandsetPortrait);
          this.viewMode.set(isHandset ? 'vertical' : 'horizontal');
        });
      }, 100);
    };
    window.addEventListener('resize', resizeHandler);

    this.destroyRef.onDestroy(() => {
      sub.unsubscribe();
      window.removeEventListener('resize', resizeHandler);
      if (this.resizeTimeout) {
        window.clearTimeout(this.resizeTimeout);
      }
    });

    // react to viewMode changes
    effect(() => {
      const mode = this.viewMode();
      this.applyClasses(mode);
      this.applyStyles(mode);
      // Force change detection after applying changes
      this.cdr.detectChanges();
    });
  }

  private applyClasses(mode: ViewMode) {
    // remove previously applied classes
    for (const c of this.prevApplied) {
      if (c) this.r2.removeClass(this.host.nativeElement, c);
    }
    // add new classes
    const toAdd = (this.classMap[mode] ?? '').split(' ').filter(Boolean);
    toAdd.forEach(c => this.r2.addClass(this.host.nativeElement, c));
    this.prevApplied = toAdd;
  }

  private applyStyles(mode: ViewMode) {
    const el = this.host.nativeElement;

    for (const styleKey of this.prevAppliedStyles) {
      this.r2.removeStyle(el, styleKey);
    }
    this.prevAppliedStyles = [];

    const map = this.kbResponsiveStyle?.[mode];
    if (!map) return;

    Object.entries(map).forEach(([k, v]) => {
      this.r2.setStyle(el, k, v);
      this.prevAppliedStyles.push(k);
    });
  }

  ngOnDestroy() {}
}
